import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { GroupsAsync } from 'store/group/groupAsync';
import { groupsActions } from 'store/group/groupSlice';
import { getGroupItem } from 'store/group/groupSelectors';
import MediaFormats from 'types/MediaFormats';
// Components
import { Loader } from 'components/Utilities';
import { Button } from 'components/Buttons';
import { GroupToolbar } from 'components/GroupToolbar';
import FormSidebar from './FormSidebar';
import SectionMain from './SectionMain';
import SectionPrizes from './SectionPrizes';
import SectionTheme from './SectionTheme';
import SectionImages from './SectionImages';
import SectionCallToAction from './SectionCallToAction';
import SectionLeaderboard from './SectionLeaderboard';
import SectionLiveLeaderboard from './SectionLiveLeaderboard';

interface IForm {
  name: string;
  logoImage: string;
  logoFormat: MediaFormats;
  logoImageBackgroundColor: string;
  backgroundImage: string;
  passcode: string;
  callToActionImageUrl: string;
  callToActionLinkUrl: string;
  callToActionButtonText: string;
  prizes: { prize:string }[];
  publicLeaderboard: {
    enabled: boolean;
    teamEnabled: boolean;
    portraitWallpaperImage: string;
    wallpaperImage: string;
    primaryColor: string;
    textColor: string;
    backgroundColor: string;
  };
  theme: {
    primaryColor: string;
    primaryFontColor: string;
    primaryButtonGradientStart: string;
    primaryButtonGradientEnd: string;
    primaryButtonFontColor: string;
  };
  resultsPage: {
    placingGradientStart: string;
    placingGradientEnd: string;
    placingFontColor: string;
    currentPlacingGradientStart: string;
    currentPlacingGradientEnd: string;
    currentPlacingFontColor: string;
  }
}

const defaultPublicLeaderboard = {
  enabled: true,
  teamEnabled: true,
  wallpaperImage: '',
  primaryColor: '#f99014',
  textColor: '#fff',
  backgroundColor: 'rgba(0,0,0,0.6)'
};

const GroupFormPage:FC = () => {
  const { t } = useTranslation();
  const { groupId } = useParams<{ groupId:string }>();

  const dispatch = useAppDispatch();

  const [ isLoading, setIsLoading ] = useState(false);
  const groupItem = useAppSelector(getGroupItem);

  const methods = useForm<IForm>({
    defaultValues: {
      name: '',
      logoImage: '',
      logoFormat: MediaFormats.Logo,
      logoImageBackgroundColor: 'rgba(255, 255, 255, 0)',
      backgroundImage: '',
      passcode: '',
      callToActionImageUrl: '',
      callToActionLinkUrl: '',
      callToActionButtonText: '',
      prizes: [{ prize: '' }],
      publicLeaderboard: defaultPublicLeaderboard,
      theme: {
        primaryColor: '#000',
        primaryFontColor: '#fff',
        primaryButtonGradientStart: '#facc22',
        primaryButtonGradientEnd: '#f83600',
        primaryButtonFontColor: '#282728'
      },
      resultsPage: {
        placingGradientStart: '#be7eea',
        placingGradientEnd: '#687fd9',
        placingFontColor: '#242324',
        currentPlacingGradientStart: '#e3722d',
        currentPlacingGradientEnd: '#f4d549',
        currentPlacingFontColor: '#242324'
      }
    }
  });

  const onSubmit = methods.handleSubmit((data:IForm) => {
    setIsLoading(true);

    const newData = {
      ...data,
      prizes: data.prizes.map((prize:any) => prize.prize)
    };

    dispatch(GroupsAsync.updateGroup({ groupId, groupData: newData })).unwrap()
      .finally(() => setIsLoading(false));
  });

  useEffect(() => {
    if (!groupItem) return;
    methods.reset({
      name: groupItem.name ?? '',
      logoImage: groupItem.logoImage ?? '',
      logoFormat: groupItem.logoFormat ?? MediaFormats.Logo,
      logoImageBackgroundColor: groupItem.logoImageBackgroundColor ?? 'rgba(255, 255, 255, 0)',
      backgroundImage: groupItem.backgroundImage ?? '',
      passcode: groupItem.passcode ?? '',
      callToActionImageUrl: groupItem.callToActionImageUrl ?? '',
      callToActionLinkUrl: groupItem.callToActionLinkUrl ?? '',
      callToActionButtonText: groupItem.callToActionButtonText ?? '',
      prizes: groupItem.prizes?.map((prize:string) => ({ prize })),
      publicLeaderboard: groupItem.publicLeaderboard
        ? { ...defaultPublicLeaderboard, ...groupItem.publicLeaderboard }
        : defaultPublicLeaderboard,
      theme: {
        primaryColor: groupItem.theme.primaryColor ?? '#000',
        primaryFontColor: groupItem.theme.primaryFontColor ?? '#fff',
        primaryButtonGradientStart: groupItem.theme.primaryButtonGradientStart ?? '#facc22',
        primaryButtonGradientEnd: groupItem.theme.primaryButtonGradientEnd ?? '#f83600',
        primaryButtonFontColor: groupItem.theme.primaryButtonFontColor ?? '#282728'
      },
      resultsPage: {
        placingGradientStart: groupItem.resultsPage.placingGradientStart ?? '#be7eea',
        placingGradientEnd: groupItem.resultsPage.placingGradientEnd ?? '#687fd9',
        placingFontColor: groupItem.resultsPage.placingFontColor ?? '#242324',
        currentPlacingGradientStart: groupItem.resultsPage.currentPlacingGradientStart ?? '#e3722d',
        currentPlacingGradientEnd: groupItem.resultsPage.currentPlacingGradientEnd ?? '#f4d549',
        currentPlacingFontColor: groupItem.resultsPage.currentPlacingFontColor ?? '#242324'
      }
    });
    // eslint-disable-next-line
  }, [groupItem]);

  useEffect(() => {
    if ( groupId ) dispatch(GroupsAsync.fetchGroupById(groupId));
    return () => {
      if ( groupId ) dispatch(groupsActions.setInitialField('item'));
    }
    // eslint-disable-next-line
  }, []);

  if (!groupItem) return <Loader />;
  return (
    <FormProvider {...methods}>
      <form
        className="
          flex flex-col container w-full h-full max-w-screen-desktop
          overflow-x-hidden
          overflow-y-auto tablet:overflow-y-hidden
        "
        onSubmit={onSubmit}
        noValidate
      >
        <div className="pt-4 flex items-center mb-4">
          <div className="flex-grow">
            <GroupToolbar />
          </div>
          <Button
            type="submit"
            variant="contained"
            loading={isLoading}
          >{t('buttons.save')}</Button>
        </div>
        <div className="
          overflow-y-visible tablet:overflow-y-auto
          block tablet:flex
          flex-col tablet:flex-row tablet:gap-6
          mb-4 flex-grow
        ">
          <FormSidebar />
          <div className="w-full overflow-y-hidden tablet:overflow-y-auto overflow-x-hidden" react-action="scroll">
            <SectionMain />
            <SectionPrizes />
            <SectionTheme />
            <SectionImages />
            <SectionCallToAction />
            <SectionLeaderboard />
            <SectionLiveLeaderboard />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default GroupFormPage;
